import type { ReactNode } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { Guide } from './types';
import { useWikiHtml } from './useWikiHtml';

/**
 * The conclusion screen: the guide's closing prose in a single readable column,
 * plus a forward CTA out to the full guide so the embed isn't a dead end.
 *
 * Like StepScreen, `heading` labels the section inline for the full (all-steps)
 * embed, where the card has no header naming it. The paginated app names it in
 * EmbedHeader and passes none.
 */
export function ConclusionScreen({
   guide,
   heading,
   className,
   footer,
}: {
   guide: Guide;
   heading?: string;
   className?: string;
   // The paginated app's pager; the full embed scrolls instead and passes none.
   footer?: ReactNode;
}) {
   // Conclusion prose is wiki HTML: force link targets + hydrate any video embed.
   const ref = useWikiHtml<HTMLDivElement>(guide.conclusion_rendered);
   return (
      <section className={cn('mx-auto block max-w-[760px] px-6 pb-6 pt-20', className)}>
         {heading && (
            <h2 className="m-0 mb-3 text-xl font-semibold leading-tight text-gray-950">
               {heading}
            </h2>
         )}
         {guide.conclusion_rendered && (
            <div
               ref={ref}
               className="text-base leading-[1.6] text-gray-950 [&_a:hover]:underline [&_a]:text-brand-500 [&_b]:font-semibold [&_li+li]:mt-1.5 [&_ol]:mb-4 [&_ol]:pl-6 [&_p]:mb-4 [&_strong]:font-semibold [&_ul]:mb-4 [&_ul]:pl-6"
               // Rendered by the guide API's wiki renderer (server-sanitized HTML).
               dangerouslySetInnerHTML={{ __html: guide.conclusion_rendered }}
            />
         )}
         {guide.url && (
            <div className="mt-6">
               <a
                  href={guide.url}
                  target="_parent"
                  className="button-primary button-lg no-underline"
               >
                  {_js('View the full guide')}
                  <FaIcon name="arrow-up-right-from-square" aria-hidden />
               </a>
            </div>
         )}
         {footer}
      </section>
   );
}
